import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import {
  getAgentProviders,
  getProviderOptionsSync,
  getAuthMethodOptionsForTargetSync
} from '../AuthenticationFields/agentProvidersService';
import DynamicAuthenticationFields from '../AuthenticationFields/DynamicAuthenticationFields';
import AdvancedSettingsSection from '../AdvancedSettingsSection';
import styles from '../ScannerConfigurationForm.css';

/**
 * ConnectionSection Component
 *
 * Renders the connection configuration section with:
 * - Agent provider selector
 * - Authentication method selector
 * - Authentication credentials fields
 * - Connection test button and status
 * - Advanced settings (email notifications)
 */
const ConnectionSection = ({
  mode,
  formData,
  connectionStatus,
  connectionError,
  connectionHeaderError,
  showAdvancedSettings,
  onChange,
  onTestConnection,
  onToggleAdvancedSettings,
  onToggleEmailNotifications,
  onAddEmail,
  onRemoveEmail,
  context
}) => {
  const [providersLoaded, setProvidersLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (context) {
      getAgentProviders(context).then(() => {
        if (!cancelled) setProvidersLoaded(true);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [context]);

  const targetSystemOptions = useMemo(
    () => getProviderOptionsSync(),
    [providersLoaded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const authMethodOptions = useMemo(() => {
    if (!formData.targetSystem) return [];

    return getAuthMethodOptionsForTargetSync(formData.targetSystem);
  }, [formData.targetSystem, providersLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Get target system display name for error messages.
   */
  const targetSystemName = useMemo(() => {
    const option = targetSystemOptions.find(
      (opt) => opt.value === formData.targetSystem
    );

    return option?.label || 'Unknown System';
  }, [formData.targetSystem, targetSystemOptions]);

  // Build default error message - kept generic to avoid tight coupling with field names
  const defaultErrorMessage = `We couldn't connect to ${targetSystemName}. Please verify your authentication credentials and try again.`;

  return (
    <section className={styles.section}>
      <div className={styles['section-title']}>
        Connection Configuration
        <Tooltip
          content="Configure the connection to the external system to discover agents."
          position="top"
          testId="connection-title-label-tooltip"
          theme={styles}
        >
          <span>
            <Icon name="info-small" size={18} className={styles.infoIcon} />
          </span>
        </Tooltip>
      </div>
      <div className={styles.field}>
        <label htmlFor="target-system" className={styles.label}>
          Agent Provider <span className={styles.required}>*</span>
          {mode !== 'edit' && (
            <span>
              <Tooltip
                content="Select the platform to discover and import agents."
                position="top"
                testId="target-system-label-tooltip"
                theme={styles}
              >
                <Icon name="info-small" size={18} className={styles.infoIcon} />
              </Tooltip>
            </span>
          )}
        </label>
        <div className={styles['field-content']}>
          <Select
            id="target-system"
            name="targetSystem"
            testId="target-system"
            value={formData.targetSystem}
            placeholder="Select..."
            options={targetSystemOptions}
            onChange={(event) =>
              onChange('targetSystem', event.itemsSelected?.value || '')
            }
            required
            disabled={mode === 'edit'}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="authentication-method" className={styles.label}>
          Authentication Method <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="authentication-method"
            name="authMethod"
            testId="authentication-method"
            value={formData.authMethod}
            placeholder="Select..."
            options={authMethodOptions}
            onChange={(event) =>
              onChange('authMethod', event.itemsSelected?.value || '')
            }
            required
            disabled={!formData.targetSystem}
          />
        </div>
      </div>

      {/* Authentication Credentials - rendered dynamically from Agent Providers JSON */}
      <DynamicAuthenticationFields
        mode={mode}
        targetSystem={formData.targetSystem}
        authMethod={formData.authMethod}
        formData={formData}
        onChange={onChange}
        context={context}
      />

      {/* Check Connection Status */}
      {formData.authMethod && (
        <div className={styles.field}>
          <span className={styles['label-text']}>Verify Authentication</span>
          <div className={styles['field-content']}>
            {connectionStatus === 'idle' && (
              <p className={styles['connection-hint']}>
                Test the connection to verify authentication
              </p>
            )}

            {connectionStatus === 'success' && (
              <p className={styles['status-success']}>
                ✓ Connection verified successfully
              </p>
            )}

            {connectionStatus === 'error' && (
              <p className={styles['status-error']}>
                {connectionHeaderError
                  ? `✕ ${connectionHeaderError}`
                  : `✕ ${defaultErrorMessage} ${connectionError ? ` ${connectionError}` : ''}`}
              </p>
            )}

            {/* Button - Always Visible */}
            <Button
              type="button"
              kind="primary"
              onClick={onTestConnection}
              disabled={connectionStatus === 'checking'}
              data-test-id="test-connection-button"
            >
              {connectionStatus === 'checking'
                ? 'Testing connection...'
                : 'Test connection'}
            </Button>
          </div>
        </div>
      )}

      {/* Advanced Settings */}
      <AdvancedSettingsSection
        showAdvancedSettings={showAdvancedSettings}
        onToggleAdvancedSettings={onToggleAdvancedSettings}
        emailNotificationsEnabled={formData.emailNotificationsEnabled}
        onToggleEmailNotifications={onToggleEmailNotifications}
        recipientEmails={formData.recipientEmails}
        onAddEmail={onAddEmail}
        onRemoveEmail={onRemoveEmail}
      />
    </section>
  );
};

ConnectionSection.propTypes = {
  /** Form mode: 'add' or 'edit' */
  mode: PropTypes.oneOf(['add', 'edit']),
  /** Form data object containing all form values */
  formData: PropTypes.shape({
    targetSystem: PropTypes.string,
    authMethod: PropTypes.string,
    emailNotificationsEnabled: PropTypes.bool,
    recipientEmails: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  /** Current connection test status */
  connectionStatus: PropTypes.oneOf(['idle', 'checking', 'success', 'error'])
    .isRequired,
  /** Connection test error message */
  connectionError: PropTypes.string,
  /** Error message from x-error-message response header */
  connectionHeaderError: PropTypes.string,
  /** Whether advanced settings section is expanded */
  showAdvancedSettings: PropTypes.bool.isRequired,
  /** Handler for form field changes */
  onChange: PropTypes.func.isRequired,
  /** Handler for test connection button */
  onTestConnection: PropTypes.func.isRequired,
  /** Handler to toggle advanced settings visibility */
  onToggleAdvancedSettings: PropTypes.func.isRequired,
  /** Handler to toggle email notifications */
  onToggleEmailNotifications: PropTypes.func.isRequired,
  /** Handler to add a new email */
  onAddEmail: PropTypes.func.isRequired,
  /** Handler to remove an email */
  onRemoveEmail: PropTypes.func.isRequired,
  /** Request context with authorization */
  context: PropTypes.object
};

export default ConnectionSection;
